import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchEvents,
  addEvent,
  deleteEvent,
  fetchSubscriptions,
  fetchContacts,
  fetchContactForms,
  fetchIssues,
  addIssue,
  deleteIssue,
  updateIssue
  , fetchSiteSettings, upsertSiteSetting
  , fetchVideos, addVideo, deleteVideo, updateVideo
  , fetchCountdowns, createCountdown, updateCountdown, deleteCountdown
} from "./api"; // Adjust the import based on your file structure

import AddContactForm from './AddContactForm';       // adjust path if needed
import EditContactForm from './EditContactForm';     // adjust path if needed
import AddEventForm from "./AddEventForm";
import EditEventForm from "./EditEventForm";
import AddBlogForm from "./AddBlogForm";
import EditBlogForm from "./EditBlogForm";
import GalleryImagesSection from "./GalleryImagesSection";
import BulkEmailSection from "./BulkEmailSection";
import RsvpSubmissions from "./RsvpSubmissions";
import EditSubscriptionForm from './EditSubscriptionForm';
import MediaManager from './MediaManager';
import AdminEndorsements from "./AdminEndorsements";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [showContactModal, setShowContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editContactId, setEditContactId] = useState(null);

  // Map URL path to section name
  const pathToSection = (pathname) => {
    if (pathname.endsWith("/events")) return "Events";
    if (pathname.endsWith("/subscriptions")) return "Subscriptions";
    if (pathname.endsWith("/contacts")) return "Contacts";
    if (pathname.endsWith("/contact-forms")) return "Contact Forms";
    if (pathname.endsWith("/news")) return "Blogs";
    if (pathname.endsWith("/issues")) return "Ren on Issues";
    if (pathname.endsWith("/endorsements")) return "Endorsements";
    if (pathname.endsWith("/videos")) return "Videos";
    if (pathname.endsWith("/site")) return "Site";
    if (pathname.endsWith("/gallery")) return "Slider or Gallery Images";
  if (pathname.endsWith("/media")) return "Media";
    if (pathname.endsWith("/bulk-email")) return "Bulk Email";
    if (pathname.endsWith("/rsvp-submissions")) return "RSVP Submissions";
    if (pathname.endsWith("/mails")) return "Mails";
    return "Dashboard";
  };
  const sectionToPath = (section) => {
    switch (section) {
      case "Events": return "/dashboard/events";
      case "Subscriptions": return "/dashboard/subscriptions";
      case "Contacts": return "/dashboard/contacts";
      case "Contact Forms": return "/dashboard/contact-forms";
      case "Blogs": return "/dashboard/news";
      case "News": return "/dashboard/news";
      case "Ren on Issues": return "/dashboard/issues";
        case "Endorsements": return "/dashboard/endorsements";
      case "Videos": return "/dashboard/videos";
      case "Slider or Gallery Images": return "/dashboard/gallery";
  case "Media": return "/dashboard/media";
      case "Bulk Email": return "/dashboard/bulk-email";
      case "RSVP Submissions": return "/dashboard/rsvp-submissions";
      case "Mails": return "/dashboard/mails";
      case "Site": return "/dashboard/site";
      default: return "/dashboard";
    }
  };
  const [activePage, setActivePage] = useState(pathToSection(location.pathname));

  // Sync activePage with URL
  useEffect(() => {
    setActivePage(pathToSection(location.pathname));
    // eslint-disable-next-line
  }, [location.pathname]);

  // Event state and logic
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEventId, setEditEventId] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", start_date: "", end_date: "" });

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsError, setBlogsError] = useState("");
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [showEditBlogModal, setShowEditBlogModal] = useState(false);
  const [editBlogId, setEditBlogId] = useState(null);

  // Issues state
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState("");
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({ title: "", image: null, description: "" });
  const [showEditIssueModal, setShowEditIssueModal] = useState(false);
  const [editIssueId, setEditIssueId] = useState(null);
  const [editIssueForm, setEditIssueForm] = useState({ title: "", image: null, description: "" });
  const [issuePopoverId, setIssuePopoverId] = useState(null);

  // Site settings state
  const [siteSetting, setSiteSetting] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState("");
  const [siteVideoUrl, setSiteVideoUrl] = useState("");

  // Countdowns state
  const [countdowns, setCountdowns] = useState([]);
  const [countdownsLoading, setCountdownsLoading] = useState(false);
  const [countdownsError, setCountdownsError] = useState("");
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const [countdownForm, setCountdownForm] = useState({
    id: null,
    title: "",
    end_date: "",
    is_active: true
  });

  // Videos state
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: "", category: "", video: null });
  const [showEditVideoModal, setShowEditVideoModal] = useState(false);
  const [editVideoId, setEditVideoId] = useState(null);
  const [editVideoForm, setEditVideoForm] = useState({ title: "", category: "", video: null });
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  // Subscription state
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [editSubscriptionId, setEditSubscriptionId] = useState(null);
  // Blog API helpers
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const fetchBlogs = async () => {
    const res = await fetch(`${API_URL}/api/blogs/`);
    if (!res.ok) throw new Error("Failed to fetch blogs");
    return await res.json();
  };
  const deleteBlog = async (id) => {
    const res = await fetch(`${API_URL}/api/blogs/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
    });
    if (!res.ok) throw new Error("Failed to delete blog");
  };

  const loadBlogs = async () => {
    setBlogsLoading(true);
    setBlogsError("");
    try {
      const data = await fetchBlogs();
      setBlogs(data);
    } catch (e) {
      setBlogsError("Failed to load blogs");
    }
    setBlogsLoading(false);
  };
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState("");

  // Contact state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");

  const [contactForms, setContactForms] = useState([]);
  const [contactFormsLoading, setContactFormsLoading] = useState(false);
  const [contactFormsError, setContactFormsError] = useState("");

  // Date formatting function
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscription?")) return;
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/${id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!res.ok) throw new Error("Failed to delete subscription");
      await loadSubscriptions();
    } catch (err) {
      alert("Error deleting subscription: " + err.message);
    }
  };

  useEffect(() => {
    if (activePage === "Events" || activePage === "Dashboard") {
      loadEvents();
    }
    if (activePage === "Subscriptions") {
      loadSubscriptions();
    }
    if (activePage === "Contacts") {
      loadContacts();
    }
    if (activePage === "Contact Forms") {
      loadContactForms();
    }
    if (activePage === "Blogs") {
      loadBlogs();
    }
    if (activePage === "Ren on Issues") {
      loadIssues();
    }
    if (activePage === "Site") {
      loadSiteSettings();
      loadCountdowns();
    }
    if (activePage === "Videos") {
      loadVideos();
    }
    // eslint-disable-next-line
  }, [activePage]);

  const loadSiteSettings = async () => {
    setSiteLoading(true);
    setSiteError("");
    try {
      const s = await fetchSiteSettings();
      setSiteSetting(s || null);
      setSiteVideoUrl((s && s.home_video_url) || "");
    } catch (e) {
      setSiteError("Failed to load site settings");
    }
    setSiteLoading(false);
  };

  const handleSaveSite = async () => {
    setSiteLoading(true);
    try {
      await upsertSiteSetting({ home_video_url: siteVideoUrl });
      await loadSiteSettings();
      alert('Site settings saved');
    } catch (e) {
      setSiteError('Failed to save site settings');
    }
    setSiteLoading(false);
  };

  const loadCountdowns = async () => {
    setCountdownsLoading(true);
    setCountdownsError("");
    try {
      const data = await fetchCountdowns();
      setCountdowns(data);
    } catch (e) {
      setCountdownsError("Failed to load countdowns");
    }
    setCountdownsLoading(false);
  };

  const handleAddCountdown = async (e) => {
    e.preventDefault();
    setCountdownsLoading(true);
    try {
      if (countdownForm.id) {
        await updateCountdown(countdownForm.id, countdownForm);
      } else {
        await createCountdown(countdownForm);
      }
      setShowCountdownModal(false);
      setCountdownForm({ id: null, title: "", end_date: "", is_active: true });
      await loadCountdowns();
    } catch (e) {
      setCountdownsError("Failed to save countdown");
    }
    setCountdownsLoading(false);
  };

  const handleDeleteCountdown = async (id) => {
    if (!window.confirm("Are you sure you want to delete this countdown?")) return;
    setCountdownsLoading(true);
    try {
      await deleteCountdown(id);
      await loadCountdowns();
    } catch (e) {
      setCountdownsError("Failed to delete countdown");
    }
    setCountdownsLoading(false);
  };

  const openCountdownModal = (c = null) => {
    if (c) {
      setCountdownForm({
        id: c.id,
        title: c.title,
        end_date: c.end_date, 
        is_active: c.is_active
      });
    } else {
      setCountdownForm({ id: null, title: "", end_date: "", is_active: true });
    }
    setShowCountdownModal(true);
  };

  const getLocalDateTimeInputValue = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  const handleDateChange = (e) => {
    const localValue = e.target.value;
    if (!localValue) {
      setCountdownForm({...countdownForm, end_date: ""});
      return;
    }
    const date = new Date(localValue);
    setCountdownForm({...countdownForm, end_date: date.toISOString()});
  };



  const loadVideos = async () => {
    setVideosLoading(true);
    setVideosError("");
    try {
      const data = await fetchVideos();
      setVideos(data);
    } catch (e) {
      setVideosError("Failed to load videos");
    }
    setVideosLoading(false);
  };

  const handleVideoInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'video') {
      setVideoForm({ ...videoForm, video: files && files[0] ? files[0] : null });
    } else {
      setVideoForm({ ...videoForm, [name]: value });
    }
  };

  const handleEditVideoInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'video') {
      setEditVideoForm({ ...editVideoForm, video: files && files[0] ? files[0] : null });
    } else {
      setEditVideoForm({ ...editVideoForm, [name]: value });
    }
  };

  const handleAddVideo = async (e) => {
    e.preventDefault();
    setIsUploadingVideo(true);
    setVideoUploadProgress(0);
    setVideosError("");
    
    const fd = new FormData();
    fd.append('title', videoForm.title);
    fd.append('category', videoForm.category);
    if (videoForm.video) fd.append('video', videoForm.video);
    
    try {
      // Use XMLHttpRequest for progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setVideoUploadProgress(percentComplete);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error('Upload failed'));
          }
        });
        
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        
        xhr.open('POST', `${API_URL}/api/videos/`);
        const token = localStorage.getItem('access_token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(fd);
      });
      
      setShowVideoModal(false);
      setVideoForm({ title: "", category: "", video: null });
      setVideoUploadProgress(0);
      await loadVideos();
    } catch (e) {
      setVideosError("Failed to add video: " + e.message);
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const openEditVideo = (video) => {
    setEditVideoId(video.id);
    setEditVideoForm({
      title: video.title || "",
      category: video.category || "",
      video: null,
    });
    setShowEditVideoModal(true);
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    setIsUploadingVideo(true);
    setVideoUploadProgress(0);
    setVideosError("");
    
    const fd = new FormData();
    fd.append('title', editVideoForm.title);
    fd.append('category', editVideoForm.category);
    if (editVideoForm.video) fd.append('video', editVideoForm.video);
    
    try {
      // Use XMLHttpRequest for progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = Math.round((e.loaded / e.total) * 100);
            setVideoUploadProgress(percentComplete);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error('Upload failed'));
          }
        });
        
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        
        xhr.open('PATCH', `${API_URL}/api/videos/${editVideoId}/`);
        const token = localStorage.getItem('access_token');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(fd);
      });
      
      setShowEditVideoModal(false);
      setEditVideoId(null);
      setVideoUploadProgress(0);
      await loadVideos();
    } catch (e) {
      setVideosError("Failed to update video: " + e.message);
    } finally {
      setIsUploadingVideo(false);
    }
  };

  // Video delete handler (called from the videos table)
  const handleDeleteVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await deleteVideo(id);
      await loadVideos();
    } catch (e) {
      setVideosError("Failed to delete video");
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      await deleteBlog(id);
      loadBlogs();
    } catch (e) {
      setBlogsError("Failed to delete blog");
    }
  };

  const renderBlogsTable = () => (
    <>
      <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
        <h1>Blogs</h1>
        <button className="btn-primary-primary" onClick={() => setShowBlogModal(true)}>
          Add New Blog
        </button>
      </div>
      {showBlogModal && (
        <div className="modal-overlay" onClick={() => setShowBlogModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <AddBlogForm onSuccess={() => { setShowBlogModal(false); loadBlogs(); }} />
          </div>
        </div>
      )}
      {showEditBlogModal && (
        <div className="modal-overlay" onClick={() => setShowEditBlogModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <EditBlogForm
              blogId={editBlogId}
              onSuccess={() => { setShowEditBlogModal(false); loadBlogs(); }}
              onCancel={() => setShowEditBlogModal(false)}
            />
          </div>
        </div>
      )}
      {blogsError && <div style={{ color: 'red' }}>{blogsError}</div>}
      {blogsLoading ? (
        <div>Loading blogs...</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {blogs.length === 0 ? (
              <tr><td colSpan="3">No blogs found.</td></tr>
            ) : (
              blogs.map(blog => (
                <tr key={blog.id}>
                  <td>{blog.title}</td>
                  <td>{blog.category}</td>
                  <td style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => handleDeleteBlog(blog.id)}>Delete</button>
                    <button className="btn-primary-primary" style={{ marginLeft: 8 }} onClick={() => { setEditBlogId(blog.id); setShowEditBlogModal(true); }}>Edit</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </>
  );

  const renderSiteSection = () => (
    <div>
      <h2>Site Settings</h2>
      {siteError && <div style={{ color: 'red' }}>{siteError}</div>}
      <div style={{ maxWidth: 600, display: 'none' }}>
        <label>Home page video URL</label>
        <input className="input" type="url" value={siteVideoUrl} onChange={(e) => setSiteVideoUrl(e.target.value)} placeholder="https://..." />
        <div style={{ marginTop: 10 }}>
          <button className="btn-primary-primary" onClick={handleSaveSite} disabled={siteLoading}>Save</button>
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
          <h3>Countdowns</h3>
          <button className="btn-primary-primary" onClick={() => openCountdownModal()}>
            Add Countdown
          </button>
        </div>
        
        {countdownsError && <div style={{ color: 'red' }}>{countdownsError}</div>}
        
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {countdowns.map(c => (
              <tr key={c.id}>
                <td>{c.title}</td>
                <td>{new Date(c.end_date).toLocaleString()}</td>
                <td>{c.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <button className="btn-secondary" onClick={() => openCountdownModal(c)}>Edit</button>
                  <button className="btn-danger" onClick={() => handleDeleteCountdown(c.id)} style={{ marginLeft: 10 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCountdownModal && (
        <div className="modal-overlay" onClick={() => setShowCountdownModal(false)}>
          <div className="modal-content" style={{ width: "500px" }} onClick={e => e.stopPropagation()}>
            <h2>{countdownForm.id ? 'Edit' : 'Add'} Countdown</h2>
            <form onSubmit={handleAddCountdown}>
              <label>Title</label>
              <input 
                className="input" 
                type="text" 
                value={countdownForm.title} 
                onChange={(e) => setCountdownForm({...countdownForm, title: e.target.value})} 
                required 
              />
              
              <label>End Date</label>
              <input 
                className="input" 
                type="datetime-local" 
                value={getLocalDateTimeInputValue(countdownForm.end_date)} 
                onChange={handleDateChange} 
                required 
              />
              
              <div style={{ marginTop: 10, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={countdownForm.is_active} 
                    onChange={(e) => setCountdownForm({...countdownForm, is_active: e.target.checked})} 
                    style={{ width: 'auto' }}
                  />
                  Active
                </label>
              </div>

              <div className="horizontal gap-10">
                <button type="submit" className="btn-primary-primary" disabled={countdownsLoading}>Save</button>
                <button type="button" className="btn-secondary" onClick={() => setShowCountdownModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  const renderVideosTable = () => (
    <>
      <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
        <h1>Videos</h1>
        <button className="btn-primary-primary" onClick={() => setShowVideoModal(true)}>
          Add Video
        </button>
      </div>
      {showVideoModal && (
        <div className="modal-overlay" onClick={() => !isUploadingVideo && setShowVideoModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <h2>Add Video</h2>
            <form onSubmit={handleAddVideo}>
              <input 
                className="input" 
                type="text" 
                name="title" 
                value={videoForm.title} 
                onChange={handleVideoInputChange} 
                placeholder="Title" 
                required 
                disabled={isUploadingVideo}
              />
              <input 
                className="input" 
                type="text" 
                name="category" 
                value={videoForm.category} 
                onChange={handleVideoInputChange} 
                placeholder="Category" 
                disabled={isUploadingVideo}
              />
              <input 
                className="input" 
                type="file" 
                name="video" 
                accept="video/*" 
                onChange={handleVideoInputChange} 
                required 
                disabled={isUploadingVideo}
              />
              
              {isUploadingVideo && (
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '10px'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                      Uploading video...
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#d62828' }}>
                      {videoUploadProgress}%
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${videoUploadProgress}%`, 
                      height: '100%', 
                      backgroundColor: '#d62828',
                      transition: 'width 0.3s ease',
                      borderRadius: '4px'
                    }} />
                  </div>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    Please wait while your video is being uploaded and processed...
                  </p>
                </div>
              )}
              
              <div className="horizontal gap-10" style={{ marginTop: 10 }}>
                <button 
                  className="btn-primary-primary" 
                  type="submit"
                  disabled={isUploadingVideo}
                  style={{ opacity: isUploadingVideo ? 0.6 : 1 }}
                >
                  {isUploadingVideo ? 'Uploading...' : 'Save'}
                </button>
                <button 
                  className="btn-secondary" 
                  type="button" 
                  onClick={() => setShowVideoModal(false)}
                  disabled={isUploadingVideo}
                  style={{ opacity: isUploadingVideo ? 0.6 : 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditVideoModal && (
        <div className="modal-overlay" onClick={() => !isUploadingVideo && setShowEditVideoModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <h2>Edit Video</h2>
            <form onSubmit={handleUpdateVideo}>
              <input 
                className="input" 
                type="text" 
                name="title" 
                value={editVideoForm.title} 
                onChange={handleEditVideoInputChange} 
                placeholder="Title" 
                required 
                disabled={isUploadingVideo}
              />
              <input 
                className="input" 
                type="text" 
                name="category" 
                value={editVideoForm.category} 
                onChange={handleEditVideoInputChange} 
                placeholder="Category" 
                disabled={isUploadingVideo}
              />
              <input 
                className="input" 
                type="file" 
                name="video" 
                accept="video/*" 
                onChange={handleEditVideoInputChange} 
                disabled={isUploadingVideo}
              />
              
              {isUploadingVideo && (
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '10px'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                      Uploading video...
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#d62828' }}>
                      {videoUploadProgress}%
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${videoUploadProgress}%`, 
                      height: '100%', 
                      backgroundColor: '#d62828',
                      transition: 'width 0.3s ease',
                      borderRadius: '4px'
                    }} />
                  </div>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    Please wait while your video is being uploaded and processed...
                  </p>
                </div>
              )}
              
              <div className="horizontal gap-10" style={{ marginTop: 10 }}>
                <button 
                  className="btn-primary-primary" 
                  type="submit"
                  disabled={isUploadingVideo}
                  style={{ opacity: isUploadingVideo ? 0.6 : 1 }}
                >
                  {isUploadingVideo ? 'Uploading...' : 'Update'}
                </button>
                <button 
                  className="btn-secondary" 
                  type="button" 
                  onClick={() => setShowEditVideoModal(false)}
                  disabled={isUploadingVideo}
                  style={{ opacity: isUploadingVideo ? 0.6 : 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {videosLoading ? (
        <div>Loading...</div>
      ) : videosError ? (
        <div style={{ color: 'red' }}>{videosError}</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => {
              const videoSrc = video.video && typeof video.video === 'string'
                ? (/^https?:\/\//i).test(video.video) || video.video.startsWith('//')
                  ? video.video
                  : `${API_URL}${video.video}`
                : null;
              return (
                <tr key={video.id}>
                  <td>{video.title}</td>
                  <td>{video.category || '-'}</td>
                  <td>
                    {videoSrc ? (
                      <video controls style={{ maxWidth: 200 }}>
                        <source src={videoSrc} />
                      </video>
                    ) : ('No video')}
                  </td>
                  <td style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-primary-primary" onClick={() => openEditVideo(video)}>Edit</button>
                    <button className="btn-secondary" onClick={() => handleDeleteVideo(video.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );

  // Issues CRUD
  const loadIssues = async () => {
    setIssuesLoading(true);
    setIssuesError("");
    try {
      const data = await fetchIssues();
      setIssues(data);
    } catch (e) {
      setIssuesError("Failed to load issues");
    }
    setIssuesLoading(false);
  };

  const handleDeleteIssue = async (id) => {
    if (!window.confirm("Delete this issue?")) return;
    try {
      await deleteIssue(id);
      loadIssues();
    } catch (e) {
      setIssuesError("Failed to delete issue");
    }
  };

  const handleIssueInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setIssueForm({ ...issueForm, image: files && files[0] ? files[0] : null });
    } else {
      setIssueForm({ ...issueForm, [name]: value });
    }
  };

  const handleEditIssueInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setEditIssueForm({ ...editIssueForm, image: files && files[0] ? files[0] : null });
    } else {
      setEditIssueForm({ ...editIssueForm, [name]: value });
    }
  };

  const handleAddIssue = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', issueForm.title);
    if (issueForm.image) fd.append('image', issueForm.image);
    fd.append('description', issueForm.description);
    try {
      await addIssue(fd);
      setShowIssueModal(false);
      setIssueForm({ title: "", image: null, description: "" });
      loadIssues();
    } catch (e) {
      setIssuesError("Failed to add issue");
    }
  };

  const openEditIssue = (issue) => {
    setEditIssueId(issue.id);
    setEditIssueForm({
      title: issue.title || "",
      image: null,
      description: issue.description || "",
    });
    setShowEditIssueModal(true);
  };

  const handleUpdateIssue = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', editIssueForm.title);
    if (editIssueForm.image) fd.append('image', editIssueForm.image);
    fd.append('description', editIssueForm.description);
    try {
      await updateIssue(editIssueId, fd);
      setShowEditIssueModal(false);
      setEditIssueId(null);
      loadIssues();
    } catch (e) {
      setIssuesError("Failed to update issue");
    }
  };

  const renderIssuesTable = () => (
    <>
      <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
        <h1>Ren on Issues</h1>
        <button className="btn-primary-primary" onClick={() => setShowIssueModal(true)}>
          Add Issue
        </button>
      </div>
      {showIssueModal && (
        <div className="modal-overlay" onClick={() => setShowIssueModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <h2>Add Issue</h2>
            <form onSubmit={handleAddIssue}>
              <input className="input" type="text" name="title" value={issueForm.title} onChange={handleIssueInputChange} placeholder="Title" required />
              <input className="input" type="file" name="image" accept="image/*" onChange={handleIssueInputChange} />
              <textarea className="input" name="description" value={issueForm.description} onChange={handleIssueInputChange} placeholder="Description" rows={6} required />
              <div className="horizontal gap-10" style={{ marginTop: 10 }}>
                <button className="btn-primary-primary" type="submit">Save</button>
                <button className="btn-secondary" type="button" onClick={() => setShowIssueModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditIssueModal && (
        <div className="modal-overlay" onClick={() => setShowEditIssueModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <h2>Edit Issue</h2>
            <form onSubmit={handleUpdateIssue}>
              <input className="input" type="text" name="title" value={editIssueForm.title} onChange={handleEditIssueInputChange} placeholder="Title" required />
              <input className="input" type="file" name="image" accept="image/*" onChange={handleEditIssueInputChange} />
              <textarea className="input" name="description" value={editIssueForm.description} onChange={handleEditIssueInputChange} placeholder="Description" rows={6} required />
              <div className="horizontal gap-10" style={{ marginTop: 10 }}>
                <button className="btn-primary-primary" type="submit">Update</button>
                <button className="btn-secondary" type="button" onClick={() => setShowEditIssueModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {issuesLoading ? (
        <div>Loading...</div>
      ) : issuesError ? (
        <div style={{ color: 'red' }}>{issuesError}</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => {
                const imageSrc = issue.image && typeof issue.image === 'string'
                  ? (/^https?:\/\//i).test(issue.image) || issue.image.startsWith('//')
                    ? issue.image
                    : `${API_URL}${issue.image}`
                  : null;
                return (
                  <tr key={issue.id}>
                    <td>
                      {imageSrc ? (
                        <img src={imageSrc} alt={issue.title} style={{ maxWidth: 80 }} />
                      ) : (
                        <span>No image</span>
                      )}
                    </td>
                    <td>{issue.title}</td>
                    <td>
                      <div className="desc-cell">
                        <div className="clamp-3">{issue.description}</div>
                        {issue.description && issue.description.length > 180 && (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setIssuePopoverId(issuePopoverId === issue.id ? null : issue.id)}
                          >
                            {issuePopoverId === issue.id ? 'Hide' : 'View more'}
                          </button>
                        )}
                        {issuePopoverId === issue.id && (
                          <div className="popover">
                            <div className="popover-content">
                              <div className="popover-text">{issue.description}</div>
                              <div style={{ marginTop: 8, textAlign: 'right' }}>
                                <button className="btn-secondary" type="button" onClick={() => setIssuePopoverId(null)}>Close</button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn-primary-primary" onClick={() => openEditIssue(issue)}>Edit</button>
                      <button className="btn-secondary" onClick={() => handleDeleteIssue(issue.id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  

const renderContactsTable = () => (
  <>
    <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
      <h1>Contacts</h1>
      <button className="btn-primary-primary" onClick={() => setShowContactModal(true)}>
        Add New Contact
      </button>
      <button className="btn-primary-primary" onClick={handleExportContacts}>
        Export Contacts
      </button>
    </div>

    {showContactModal && (
      <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
        <div className="modal-content" style={{ width: "60%" }} onClick={(e) => e.stopPropagation()}>
          <AddContactForm onSuccess={() => { setShowContactModal(false); loadContacts(); }} />
        </div>
      </div>
    )}

    {showEditContactModal && (
      <div className="modal-overlay" onClick={() => setShowEditContactModal(false)}>
        <div className="modal-content" style={{ width: "60%" }} onClick={(e) => e.stopPropagation()}>
          <EditContactForm
            contactId={editContactId}
            onSuccess={() => { setShowEditContactModal(false); loadContacts(); }}
            onCancel={() => setShowEditContactModal(false)}
          />
        </div>
      </div>
    )}

    {contactsError && <div style={{ color: 'red' }}>{contactsError}</div>}
    {contactsLoading ? (
      <div>Loading contacts...</div>
    ) : (
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone Number</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 ? (
            <tr><td colSpan="4">No contacts found.</td></tr>
          ) : (
            contacts.map(contact => (
              <tr key={contact.id}>
                <td>{contact.name}</td>
                <td>{contact.email}</td>
                <td>{contact.phone}</td>
                <td style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn-secondary" onClick={() => handleDeleteContact(contact.id)}>Delete</button>
                  <button className="btn-primary-primary" onClick={() => { setEditContactId(contact.id); setShowEditContactModal(true); }}>Edit</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    )}
  </>
);
const handleDeleteContact = async (id) => {
  if (!window.confirm("Are you sure you want to delete this contact?")) return;

  try {
    const res = await fetch(`${API_URL}/api/contacts/${id}/`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
    if (!res.ok) throw new Error("Failed to delete contact");
    await loadContacts();
  } catch (err) {
    alert("Error deleting contact: " + err.message);
  }
};

  const loadEvents = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchEvents();
      setEvents(data);
    } catch (e) {
      setError("Failed to load events");
    }
    setLoading(false);
  };

  const loadSubscriptions = async () => {
    setSubsLoading(true);
    setSubsError("");
    try {
      const data = await fetchSubscriptions();
      setSubscriptions(data);
    } catch (e) {
      setSubsError("Failed to load subscriptions");
    }
    setSubsLoading(false);
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    setContactsError("");
    try {
      const data = await fetchContacts();
      setContacts(data);
    } catch (e) {
      setContactsError("Failed to load contacts");
    }
    setContactsLoading(false);
  };

  const loadContactForms = async () => {
    setContactFormsLoading(true);
    setContactFormsError("");
    try {
      const data = await fetchContactForms();
      setContactForms(data);
    } catch (e) {
      setContactFormsError("Failed to load contact forms");
    }
    setContactFormsLoading(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addEvent({
        name: form.name,
        category: form.category,
        start_date: form.start_date,
        end_date: form.end_date,
      });
      setForm({ name: "", category: "", start_date: "", end_date: "" });
      setShowModal(false);
      loadEvents();
    } catch (e) {
      setError("Failed to add event");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await deleteEvent(id);
      loadEvents();
    } catch (e) {
      setError("Failed to delete event");
    }
  };

  const renderEventsTable = () => (
    <>
      <div className="horizontal gap-10 align-center" style={{ marginBottom: "20px" }}>
        <h1>Events</h1>
        <button className="btn-primary-primary" onClick={() => setShowModal(true)}>
          Add New Event
        </button>
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <AddEventForm onSuccess={() => { setShowModal(false); loadEvents(); }} />
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" style={{ width: "60%" }} onClick={e => e.stopPropagation()}>
            <EditEventForm
              eventId={editEventId}
              onSuccess={() => { setShowEditModal(false); loadEvents(); }}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        </div>
      )}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {loading ? (
        <div>Loading events...</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Category</th>
              <th>Start Date / Time</th>
              <th>End Date / Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr><td colSpan="6">No events found.</td></tr>
            ) : (
              events.map(ev => (
                <tr key={ev.id}>
                  <td>{ev.name}</td>
                  <td>{ev.category}</td>
                  <td>{formatDateTime(ev.start_date)}</td>
                  <td>{formatDateTime(ev.end_date)}</td>
                  <td>{ev.status}</td>
                  <td  style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => handleDelete(ev.id)}>Delete</button>
                    <button className="btn-primary-primary" style={{ marginLeft: 8 }} onClick={() => { setEditEventId(ev.id); setShowEditModal(true); }}>Edit</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </>
  );

  const renderSubscriptionsTable = () => (
    <>
      <h2>Subscriptions</h2>
      {subsError && <div style={{ color: 'red' }}>{subsError}</div>}
      {subsLoading ? (
        <div>Loading subscriptions...</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 ? (
              <tr><td colSpan="3">No subscriptions found.</td></tr>
            ) : (
              subscriptions.map(sub => (
                <tr key={sub.id || sub.email}>
                  <td>{sub.name}</td>
                  <td>{sub.email}</td>
                  <td style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => handleDeleteSubscription(sub.id)}>Delete</button>
                    <button className="btn-primary-primary" onClick={() => { setEditSubscriptionId(sub.id); setShowEditSubscriptionModal(true); }}>Edit</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </>
  );

  const renderContactFormsTable = () => (
    <>
      <h2>Contact Form Submissions</h2>
      {contactFormsError && <div style={{ color: 'red' }}>{contactFormsError}</div>}
      {contactFormsLoading ? (
        <div>Loading contact form submissions ...</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Message</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {contactForms.length === 0 ? (
              <tr><td colSpan="4">No contact submissions found.</td></tr>
            ) : (
              contactForms.map(contact => (
                <tr key={contact.id}>
                  <td>{contact.name}</td>
                  <td>{contact.email}</td>
                  <td style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                    {contact.message.length > 100 
                      ? `${contact.message.substring(0, 100)}...` 
                      : contact.message}
                  </td>
                  <td>{formatDateTime(contact.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </>
  );

  const renderPage = () => {
    switch (activePage) {
      case "Dashboard":
        return renderEventsTable();
      case "Subscriptions":
        return renderSubscriptionsTable();
      case "Events":
        return renderEventsTable();
      case "Contacts":
        return renderContactsTable();
      case "Contact Forms":
        return renderContactFormsTable();
      case "Blogs":
        return renderBlogsTable();
      case "Ren on Issues":
        return renderIssuesTable();
      case "Site":
        return renderSiteSection();
      case "Videos":
        return renderVideosTable();
      case "Slider or Gallery Images":
        return <GalleryImagesSection />;
      case "Endorsements":
        return <AdminEndorsements />;
      case "Media":
        return <MediaManager />;
      case "Bulk Email":
        return <BulkEmailSection />;
      case "RSVP Submissions":
        return <RsvpSubmissions />;
      case "Mails":
        return <h2>Your Mails</h2>;
      default:
        return <h2>Welcome to the Dashboard</h2>;
    }
  };

  const logout = () => {
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/'; // Redirect to login page
  };

  // Export contacts as CSV
  const handleExportContacts = () => {
    if (!contacts || contacts.length === 0) {
      alert('No contacts to export.');
      return;
    }
    // Define CSV headers
    const headers = ['Name', 'Email', 'Phone'];
    // Build CSV rows
    const rows = contacts.map(contact => [
      `"${contact.name || ''}"`,
      `"${contact.email || ''}"`,
      `"${contact.phone || ''}"`
    ]);
    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    // Create blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-screen">
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-gray-800 text-white p-5 overflow-y-auto">
        <h1 className="logo text-xl font-bold mb-6">Admin</h1>
        <nav>
          <ul className="space-y-2">
            {["Dashboard", "Events", "Blogs", "Ren on Issues", "Endorsements", "Videos", "Slider or Gallery Images", "Media", "Bulk Email", "RSVP Submissions", "Subscriptions", "Contacts", "Contact Forms"].map((page) => (
                <li
                    key={page}
                    className={activePage === page ? "active bg-gray-700 px-4 py-2 rounded" : "px-4 py-2 rounded hover:bg-gray-700 cursor-pointer"}
                    onClick={() => navigate(sectionToPath(page))}
                  >
                    {page}
                  </li>
                ))}
               <li key={'Site'} className={activePage === 'Site' ? 'active bg-gray-700 px-4 py-2 rounded' : 'px-4 py-2 rounded hover:bg-gray-700 cursor-pointer'} onClick={() => navigate('/dashboard/site')}>Site</li>
          </ul>
        </nav>
        <button className="btn-primary-primary mt-5 bg-blue-600 text-white px-4 py-2 rounded w-full" style={{marginTop: 20}} onClick={() => { logout(); window.location.href = '/'; }}>Logout</button>
      </aside>

      <main className="main-content ml-64 flex-1">
        <header className="topbar bg-white p-6 shadow">
          <h1 className="text-2xl font-bold">{activePage}</h1>
        </header>
        <div className="content-area p-8 overflow-y-auto h-[calc(100vh-80px)]">{renderPage()}</div>
      </main>

      {showEditSubscriptionModal && (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEditSubscriptionModal(false)}>
          <div className="modal-content bg-white p-6 rounded-lg w-3/5 max-h-[80vh] overflow-auto" style={{ width: "60%" }} onClick={(e) => e.stopPropagation()}>
            <EditSubscriptionForm
              subscriptionId={editSubscriptionId}
              onSuccess={() => { setShowEditSubscriptionModal(false); loadSubscriptions(); }}
              onCancel={() => setShowEditSubscriptionModal(false)}
            />
          </div>
        </div>
      )}

      {/* Modal and Table Styles */}
      <style jsx="true">{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 10px;
          width: 100%;
          max-height: 80vh;
          overflow: auto;
          max-width: 600px;
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
        }
        .input {
          width: 100%;
          margin: 5px 0;
          padding: 8px;
          border-radius: 5px;
          border: 1px solid #ccc;
        }
        .btn-primary-primary {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 5px;
          cursor: pointer;
        }
        .btn-secondary {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 5px;
          cursor: pointer;
        }
        .desc-cell { position: relative; max-width: 420px; }
        .clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          white-space: pre-wrap;
        }
        .btn-link { background: none; border: none; color: #007bff; cursor: pointer; padding: 0; margin-top: 6px; }
        .btn-link:hover { text-decoration: underline; }
        .popover {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 10;
          background: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
          width: 460px;
          margin-top: 8px;
        }
        .popover-content { padding: 12px; max-height: 300px; overflow: auto; }
        .popover-text { white-space: pre-wrap; }
      `}</style>
    </div>
  );
};

export default Dashboard;
